import { DollarSign, Receipt, Calculator } from 'lucide-react';
import { SectionHeader, EmptyState } from '../shared/InfoGrid';
import { TaxCodeBadge } from '../shared/StatusBadge';
import { getTravelerName, formatMoney } from '../utils/formatters';
import type { Prices, Traveler, Dictionaries } from '../utils/types';

interface PricingTabProps {
  prices: Prices | undefined;
  travelers: Traveler[];
  dictionaries?: Dictionaries;
}

export function PricingTab({ prices, travelers, dictionaries }: PricingTabProps) {
  if (!prices) {
    return (
      <EmptyState
        icon={<DollarSign className="h-6 w-6" />}
        title="No pricing data"
        description="This booking doesn't contain any pricing information"
      />
    );
  }

  const { unitPrices, totalPrices } = prices;
  const hasUnitPrices = unitPrices && unitPrices.length > 0;
  const hasTotalPrices = totalPrices && totalPrices.length > 0;

  if (!hasUnitPrices && !hasTotalPrices) {
    return (
      <EmptyState
        icon={<DollarSign className="h-6 w-6" />}
        title="No pricing data"
        description="Pricing information is not available for this booking"
      />
    );
  }

  return (
    <div className="space-y-4 md:space-y-8">
      {/* Unit Prices - Per Traveler */}
      {hasUnitPrices && (
        <div>
          <SectionHeader
            title="Per-Traveler Prices"
            icon={<Receipt className="h-5 w-5" />}
            count={unitPrices.length}
          />

          <div className="space-y-3 md:space-y-4">
            {unitPrices.map((unitPrice, idx) => {
              const travelerNames = unitPrice.travelerIds
                .map(id => getTravelerName(travelers, id))
                .join(', ');

              return (
                <div
                  key={idx}
                  className="rounded-lg border border-slate-200 bg-white overflow-hidden"
                >
                  {/* Header */}
                  <div className="px-3 md:px-4 py-2 md:py-3 bg-slate-50 border-b border-slate-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-slate-700">
                          {travelerNames || 'Unknown Traveler'}
                        </span>
                        <span className="text-xs text-slate-400 ml-2">
                          ({unitPrice.travelerIds.join(', ')})
                        </span>
                      </div>
                      {unitPrice.flightIds && unitPrice.flightIds.length > 0 && (
                        <div className="flex items-center gap-1">
                          {unitPrice.flightIds.map(fid => (
                            <span
                              key={fid}
                              className="text-xs font-mono bg-white px-1.5 py-0.5 rounded border border-slate-200"
                            >
                              {fid}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Price details */}
                  {unitPrice.prices.map((priceDetail, priceIdx) => (
                    <div key={priceIdx} className="p-3 md:p-4">
                      {/* Base fare */}
                      <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-slate-600">Base Fare</span>
                        <span className="text-sm font-medium text-slate-900">
                          {formatMoney(priceDetail.base.value, priceDetail.base.currencyCode)}
                        </span>
                      </div>

                      {/* Taxes */}
                      {priceDetail.taxes && priceDetail.taxes.length > 0 && (
                        <div className="border-t border-slate-100 pt-2 mt-2">
                          <div className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">
                            Taxes & Fees
                          </div>
                          <div className="space-y-1">
                            {priceDetail.taxes.map((tax, taxIdx) => (
                              <div
                                key={taxIdx}
                                className="flex items-center justify-between py-1"
                              >
                                <div className="flex items-center gap-2">
                                  <TaxCodeBadge code={tax.code} />
                                  {tax.category && (
                                    <span className="text-xs text-slate-400">
                                      ({tax.category})
                                    </span>
                                  )}
                                </div>
                                <span className="text-sm text-slate-600">
                                  {formatMoney(tax.value, tax.currencyCode)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Total taxes */}
                      <div className="flex items-center justify-between py-2 border-t border-slate-100 mt-2">
                        <span className="text-sm text-slate-600">Total Taxes</span>
                        <span className="text-sm font-medium text-slate-700">
                          {formatMoney(
                            priceDetail.totalTaxes.value,
                            priceDetail.totalTaxes.currencyCode
                          )}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Total Prices */}
      {hasTotalPrices && (
        <div>
          <SectionHeader
            title="Total Prices"
            icon={<Calculator className="h-5 w-5" />}
          />

          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-3 md:px-4 py-2 md:py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {totalPrices.map((total, idx) => (
                  <>
                    <tr key={`base-${idx}`}>
                      <td className="px-3 md:px-4 py-2 md:py-3 text-sm text-slate-600">Base Fare</td>
                      <td className="px-3 md:px-4 py-2 md:py-3 text-sm text-right font-medium text-slate-900">
                        {formatMoney(total.base.value, total.base.currencyCode)}
                      </td>
                    </tr>
                    <tr key={`tax-${idx}`}>
                      <td className="px-3 md:px-4 py-2 md:py-3 text-sm text-slate-600">Total Taxes</td>
                      <td className="px-3 md:px-4 py-2 md:py-3 text-sm text-right font-medium text-slate-700">
                        {formatMoney(total.totalTaxes.value, total.totalTaxes.currencyCode)}
                      </td>
                    </tr>
                    <tr key={`total-${idx}`} className="bg-slate-50">
                      <td className="px-3 md:px-4 py-2 md:py-3 text-sm font-semibold text-slate-900">
                        Grand Total
                      </td>
                      <td className="px-3 md:px-4 py-2 md:py-3 text-right font-bold text-base md:text-lg text-slate-900">
                        {formatMoney(
                          total.base.value + total.totalTaxes.value,
                          total.base.currencyCode
                        )}
                      </td>
                    </tr>
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Exchange prices if available */}
      {prices.exchangePrices && (
        <div>
          <SectionHeader
            title="Exchange Details"
            icon={<Receipt className="h-5 w-5" />}
          />

          <div className="rounded-lg border border-slate-200 bg-white p-3 md:p-4">
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              <div>
                <div className="text-xs text-slate-400 mb-1">Balance</div>
                <div className="font-medium text-slate-900">
                  {formatMoney(
                    prices.exchangePrices.balance.total,
                    prices.exchangePrices.balance.currencyCode
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-400 mb-1">Additional Collection</div>
                <div className="font-medium text-slate-900">
                  {formatMoney(
                    prices.exchangePrices.additionalCollection.value,
                    prices.exchangePrices.additionalCollection.currencyCode
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-400 mb-1">Penalty</div>
                <div className="font-medium text-slate-900">
                  {formatMoney(
                    prices.exchangePrices.penalty.value,
                    prices.exchangePrices.penalty.currencyCode
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-400 mb-1">Refund</div>
                <div className="font-medium text-slate-900">
                  {formatMoney(
                    prices.exchangePrices.refund.value,
                    prices.exchangePrices.refund.currencyCode
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
